import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// 定義數據模型
interface Contractor {
  id: string;
  name: string;
}

interface EntryExitPerson {
  id: string;
  name: string;
  idNumber: string; // 身份證號碼
  phone: string;
  photoUrl?: string;
  contractor: Contractor;
  jobType: string;
  applicationId: string;
  scheduledEntryTime: Date;
  entryTime?: Date;
  exitTime?: Date | null;
  status: 'waiting' | 'entered' | 'exited';
}

interface EntryExitRecord {
  id: string;
  personId: string;
  date: Date;
  applicationId: string;
  entryTime: Date;
  exitTime?: Date | undefined;
  registrar: string;
}

@Component({
  selector: 'app-entry-exit-management',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './entry-exit-management.component.html',
  styleUrls: ['./entry-exit-management.component.scss'],
  providers: [DatePipe]
})
export class EntryExitManagementComponent implements OnInit, AfterViewInit, OnDestroy {
  // QR 掃描相關
  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement') canvasElement!: ElementRef<HTMLCanvasElement>;

  // 過濾選項
  dateFilterStart: string = '';
  dateFilterEnd: string = '';
  statusFilter: string = 'all';
  contractorFilter: string = '';
  searchText: string = '';

  // 分頁相關
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalItems: number = 0;
  totalPages: number = 0;
  pageNumbers: number[] = [];

  // 數據源
  allPersonnel: EntryExitPerson[] = [];
  filteredPersonnel: EntryExitPerson[] = [];
  contractors: Contractor[] = [];

  // 狀態管理
  showQrScanner: boolean = false;
  scannerActive: boolean = false;
  selectedPerson: EntryExitPerson | null = null;
  personEntryExitHistory: EntryExitRecord[] = [];
  lastScannedPerson: EntryExitPerson | null = null;
  lastScanAction: 'entry' | 'exit' | null = null;

  // 掃描器控制
  videoStream: MediaStream | null = null;
  scanInterval: any = null;

  // RxJS
  private destroy$ = new Subject<void>();
  personDataRefresh$ = new BehaviorSubject<boolean>(true);

  constructor(private datePipe: DatePipe) {}

  ngOnInit(): void {
    // 載入承包商列表
    this.loadContractors();

    // 初始化過濾日期為當前月份
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    this.dateFilterStart = this.formatDateForInput(firstDay);
    this.dateFilterEnd = this.formatDateForInput(lastDay);

    // 訂閱人員數據刷新
    this.personDataRefresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadPersonnel();
      });

    // 載入初始數據
    this.loadPersonnel();
  }

  ngAfterViewInit(): void {
    // 在元件視圖初始化後執行的代碼
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopScanner();
  }

  // 格式化日期為 input[type=date] 可接受的格式
  formatDateForInput(date: Date): string {
    return this.datePipe.transform(date, 'yyyy-MM-dd') || '';
  }

  // 載入承包商數據
  loadContractors(): void {
    // 在實際應用中，這裡會呼叫服務來獲取數據
    this.contractors = [
      { id: 'C001', name: '台灣電子工程公司' },
      { id: 'C002', name: '冠好冷氣工程有限公司' },
      { id: 'C003', name: '宏達配電工程公司' },
      { id: 'C004', name: '盛昌水電工程行' },
      { id: 'C005', name: '明輝營造股份有限公司' }
    ];
  }

  // 載入人員數據
  loadPersonnel(): void {
    // 在實際應用中，這裡會呼叫服務來獲取數據
    this.allPersonnel = this.getMockPersonnelData();
    this.applyFilters();
  }

  // 產生模擬數據
  getMockPersonnelData(): EntryExitPerson[] {
    const personnel: EntryExitPerson[] = [];
    const today = new Date();

    // 產生一些模擬數據
    const names = ['張志明', '李大華', '王小明', '陳大同', '林小玲', '黃建國', '劉美玲', '吳志豪', '鄭雅文', '許家豪'];
    const jobTypes = ['電工', '水電工', '機械工程師', '焊接工', '安全管理員', '搬運工', '塗裝工', '資訊工程師'];
    const statuses: ('waiting' | 'entered' | 'exited')[] = ['waiting', 'entered', 'exited'];

    for (let i = 1; i <= 50; i++) {
      const contractorIndex = Math.floor(Math.random() * this.contractors.length);
      const nameIndex = Math.floor(Math.random() * names.length);
      const jobIndex = Math.floor(Math.random() * jobTypes.length);
      const statusIndex = Math.floor(Math.random() * statuses.length);

      // 產生可能的時間
      const scheduledTime = new Date();
      // 設定日期 (過去7天內的某一天)
      scheduledTime.setDate(today.getDate() - Math.floor(Math.random() * 7));
      // 設定小時 (早上8點到下午1點)
      scheduledTime.setHours(8 + Math.floor(Math.random() * 5));
      // 設定分鐘 (整點或半小時)
      scheduledTime.setMinutes(Math.random() < 0.5 ? 0 : 30);
      // 清除秒和毫秒
      scheduledTime.setSeconds(0);
      scheduledTime.setMilliseconds(0);

      let entryTime = undefined;
      let exitTime = undefined;

      const status = statuses[statusIndex];

      if (status === 'entered' || status === 'exited') {
        entryTime = new Date(scheduledTime);
        entryTime.setMinutes(entryTime.getMinutes() + Math.floor(Math.random() * 60 - 30));

        if (status === 'exited') {
          exitTime = new Date(entryTime);
          exitTime.setHours(exitTime.getHours() + 4 + Math.floor(Math.random() * 4));
        }
      }

      personnel.push({
        id: `P${(1000 + i).toString()}`,
        name: names[nameIndex],
        idNumber: `A${Math.floor(10000000 + Math.random() * 90000000)}`,
        phone: `09${Math.floor(10000000 + Math.random() * 90000000)}`,
        contractor: this.contractors[contractorIndex],
        jobType: jobTypes[jobIndex],
        applicationId: `WA${10001 + Math.floor(Math.random() * 10)}`,
        scheduledEntryTime: scheduledTime,
        entryTime,
        exitTime,
        status
      });
    }

    return personnel;
  }

  // 應用過濾器
  applyFilters(): void {
    let filtered = [...this.allPersonnel];

    // 日期過濾
    if (this.dateFilterStart && this.dateFilterEnd) {
      const startDate = new Date(this.dateFilterStart);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(this.dateFilterEnd);
      endDate.setHours(23, 59, 59, 999);

      filtered = filtered.filter(person => {
        const scheduleDate = new Date(person.scheduledEntryTime);
        return scheduleDate >= startDate && scheduleDate <= endDate;
      });
    }

    // 狀態過濾
    if (this.statusFilter !== 'all') {
      filtered = filtered.filter(person => person.status === this.statusFilter);
    }

    // 承包商過濾
    if (this.contractorFilter) {
      filtered = filtered.filter(person => person.contractor.id === this.contractorFilter);
    }

    // 關鍵字搜尋
    if (this.searchText) {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(person =>
        person.name.toLowerCase().includes(searchLower) ||
        person.idNumber.toLowerCase().includes(searchLower) ||
        person.jobType.toLowerCase().includes(searchLower) ||
        person.applicationId.toLowerCase().includes(searchLower) ||
        person.contractor.name.toLowerCase().includes(searchLower)
      );
    }

    // 更新數據和分頁
    this.totalItems = filtered.length;
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
    this.updatePageNumbers();

    // 應用分頁
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    this.filteredPersonnel = filtered.slice(startIndex, startIndex + this.itemsPerPage);
  }

  // 更新分頁數字
  updatePageNumbers(): void {
    this.pageNumbers = [];
    const maxPagesToShow = 5;

    if (this.totalPages <= maxPagesToShow) {
      // 如果總頁數小於最大顯示頁數，直接顯示全部
      for (let i = 1; i <= this.totalPages; i++) {
        this.pageNumbers.push(i);
      }
    } else {
      // 顯示部分頁數
      let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
      let endPage = startPage + maxPagesToShow - 1;

      if (endPage > this.totalPages) {
        endPage = this.totalPages;
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        this.pageNumbers.push(i);
      }
    }
  }

  // 切換頁碼
  changePage(page: number): void {
    if (page < 1 || page > this.totalPages) return;

    this.currentPage = page;
    this.applyFilters();
  }

  // 取得當前頁最後一項的索引
  getCurrentPageLastItem(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
  }

  // 重置過濾器
  resetFilters(): void {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    this.dateFilterStart = this.formatDateForInput(firstDay);
    this.dateFilterEnd = this.formatDateForInput(lastDay);
    this.statusFilter = 'all';
    this.contractorFilter = '';
    this.searchText = '';
    this.currentPage = 1;

    this.applyFilters();
  }

  // 清除搜索
  clearSearch(): void {
    this.searchText = '';
    this.applyFilters();
  }

  // 開啟 QR 掃描器
  openQrScanner(): void {
    this.showQrScanner = true;
    this.lastScannedPerson = null;
    this.lastScanAction = null;
  }

  // 關閉 QR 掃描器
  closeQrScanner(): void {
    this.showQrScanner = false;
    this.stopScanner();
  }

  // 開始掃描
  async startScanner(): Promise<void> {
    try {
      this.videoStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });

      if (this.videoElement && this.videoElement.nativeElement) {
        this.videoElement.nativeElement.srcObject = this.videoStream;
        this.videoElement.nativeElement.play();
        this.scannerActive = true;

        // 開始掃描循環
        this.scanQrCode();
      }
    } catch (error) {
      console.error('無法訪問攝像頭:', error);
      alert('無法啟動攝像頭，請確認權限是否開啟');
    }
  }

  // 暫停掃描
  pauseScanner(): void {
    this.scannerActive = false;
    if (this.scanInterval) {
      clearInterval(this.scanInterval);
      this.scanInterval = null;
    }

    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.enabled = false);
    }
  }

  // 停止掃描
  stopScanner(): void {
    this.scannerActive = false;
    if (this.scanInterval) {
      clearInterval(this.scanInterval);
      this.scanInterval = null;
    }

    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
      this.videoStream = null;
    }

    if (this.videoElement && this.videoElement.nativeElement) {
      this.videoElement.nativeElement.srcObject = null;
    }
  }

  // QR 碼掃描循環
  scanQrCode(): void {
    // 注意：這裡需要引入 jsQR 或其他 QR 掃描庫
    // 在實際應用中，您需要添加 jsQR 庫並實現實際的掃描邏輯

    this.scanInterval = setInterval(() => {
      if (!this.scannerActive) return;

      const video = this.videoElement.nativeElement;
      const canvas = this.canvasElement.nativeElement;
      const context = canvas.getContext('2d');

      if (video.readyState === video.HAVE_ENOUGH_DATA && context) {
        // 設置畫布尺寸
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // 繪製視頻幀到畫布
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // 這裡應該使用 jsQR 庫來掃描畫布上的 QR 碼
        // 假設我們獲取到了 QR 碼的內容，這裡只是模擬
        this.processQrCode('A123456789'); // 模擬掃描到身份證號
      }
    }, 1000); // 每秒掃描一次，實際應用可能需要調整
  }

  // 處理掃描到的 QR 碼
  processQrCode(qrContent: string): void {
    // 查找匹配的人員
    const person = this.allPersonnel.find(p => p.idNumber === qrContent);

    if (!person) {
      console.log('未找到匹配的人員:', qrContent);
      return;
    }

    // 根據當前狀態決定進廠或離場
    if (person.status === 'waiting') {
      this.recordEntry(person);
      this.lastScannedPerson = person;
      this.lastScanAction = 'entry';
      this.pauseScanner();
    } else if (person.status === 'entered') {
      this.recordExit(person);
      this.lastScannedPerson = person;
      this.lastScanAction = 'exit';
      this.pauseScanner();
    } else {
      console.log('人員狀態不適合記錄進出:', person.status);
    }
  }

  // 查看人員詳情
  viewDetails(person: EntryExitPerson): void {
    this.selectedPerson = person;
    this.loadPersonEntryExitHistory(person.id);
  }

  // 關閉人員詳情
  closePersonDetails(): void {
    this.selectedPerson = null;
  }

  // 載入人員進出紀錄
  loadPersonEntryExitHistory(personId: string): void {
    // 在實際應用中，這裡會呼叫服務來獲取數據
    // 這裡模擬一些歷史紀錄
    this.personEntryExitHistory = [];

    const today = new Date();

    // 生成過去7天的記錄
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);

      if (Math.random() > 0.3) { // 70% 的機率有紀錄
        const entryTime = new Date(date);
        entryTime.setHours(8 + Math.floor(Math.random() * 3));
        entryTime.setMinutes(Math.floor(Math.random() * 60));

        let exitTime = new Date(entryTime);
        exitTime.setHours(exitTime.getHours() + 6 + Math.floor(Math.random() * 4));

        // 如果是今天且狀態為已進廠，則不設置離場時間
        // if (i === 0 && this.selectedPerson?.status === 'entered') {
        //   exitTime = undefined;
        // }

        this.personEntryExitHistory.push({
          id: `E${(1000 + i).toString()}`,
          personId,
          date: new Date(date.setHours(0, 0, 0, 0)),
          applicationId: `WA${10001 + Math.floor(Math.random() * 10)}`,
          entryTime,
          exitTime,
          registrar: '系統管理員'
        });
      }
    }

    // 排序：最新的在最上面
    this.personEntryExitHistory.sort((a, b) => b.entryTime.getTime() - a.entryTime.getTime());
  }

  // 登記進廠
  recordEntry(person: EntryExitPerson): void {
    if (!this.canRecordEntry(person)) return;

    person.entryTime = new Date();
    person.status = 'entered';

    // 在實際應用中，這裡會呼叫服務來更新數據
    console.log('登記進廠:', person);
    alert(`已登記 ${person.name} 進廠`);

    // 如果是從詳情頁操作，更新歷史紀錄
    if (this.selectedPerson && this.selectedPerson.id === person.id) {
      this.loadPersonEntryExitHistory(person.id);
    }

    // 刷新列表
    this.applyFilters();
  }

  // 登記離場
  recordExit(person: EntryExitPerson): void {
    if (!this.canRecordExit(person)) return;

    person.exitTime = new Date();
    person.status = 'exited';

    // 在實際應用中，這裡會呼叫服務來更新數據
    console.log('登記離場:', person);
    alert(`已登記 ${person.name} 離場`);

    // 如果是從詳情頁操作，更新歷史紀錄
    if (this.selectedPerson && this.selectedPerson.id === person.id) {
      this.loadPersonEntryExitHistory(person.id);
    }

    // 刷新列表
    this.applyFilters();
  }

  // 檢查是否可以登記進廠
  canRecordEntry(person: EntryExitPerson): boolean {
    return person.status === 'waiting';
  }

  // 檢查是否可以登記離場
  canRecordExit(person: EntryExitPerson): boolean {
    return person.status === 'entered';
  }

  // 獲取狀態文字
  getStatusText(status: string): string {
    switch (status) {
      case 'waiting': return '尚未進廠';
      case 'entered': return '已進廠';
      case 'exited': return '已離場';
      default: return '未知';
    }
  }

  // 獲取行樣式
  getRowClass(person: EntryExitPerson): string {
    switch (person.status) {
      case 'waiting': return 'status-waiting';
      case 'entered': return 'status-entered';
      case 'exited': return 'status-exited';
      default: return '';
    }
  }

  // 計算停留時間
  calculateDuration(start: Date, end: Date): string {
    const diff = Math.abs(end.getTime() - start.getTime());
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}小時 ${minutes}分鐘`;
  }

  // 列印 QR 碼
  printQrCode(): void {
    if (!this.selectedPerson) return;

    const printWindow = window.open('', '_blank');

    if (printWindow) {
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${this.selectedPerson.idNumber}`;

      printWindow.document.write(`
        <html>
          <head>
            <title>人員 QR 碼</title>
            <style>
              body { font-family: Arial, sans-serif; text-align: center; }
              .qr-container { margin: 20px auto; max-width: 400px; padding: 20px; border: 1px solid #ccc; }
              img { max-width: 100%; }
              .info { margin-top: 20px; text-align: left; }
              .info p { margin: 5px 0; }
              @media print {
                button { display: none; }
              }
            </style>
          </head>
          <body>
            <div class="qr-container">
              <h2>人員通行證</h2>
              <img src="${qrCodeUrl}" alt="QR Code">
              <div class="info">
                <p><strong>姓名：</strong>${this.selectedPerson.name}</p>
                <p><strong>身份證號：</strong>${this.selectedPerson.idNumber}</p>
                <p><strong>公司：</strong>${this.selectedPerson.contractor.name}</p>
                <p><strong>工種：</strong>${this.selectedPerson.jobType}</p>
              </div>
              <button onclick="window.print()">列印</button>
            </div>
          </body>
        </html>
      `);

      printWindow.document.close();
    }
  }
}
